// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Durable outbound buffer for course-mapping trace batches.
//
// Modelled on services/offlineQueue.ts (versioned envelope, restart survival, age and
// count caps) but deliberately simpler and separate:
//   - traces are bulk, not realtime, so they flush over HTTP rather than the round
//     WebSocket, and there is no per-entry ack protocol — a 2xx is the ack;
//   - the unit of work is a whole batch, not a single write;
//   - AsyncStorage only. This never runs on web, so there is no localStorage branch.
//
// Batches are idempotent server-side on batchId, so replaying one is always safe.

import type { CourseTraceBatch } from '../features/course-trace/types';

type AsyncStorageLike = {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
};

type PendingBatch = {
  batch: CourseTraceBatch;
  enqueuedAt: string;
  attempts: number;
};

type Envelope = {
  version: 1;
  batches: PendingBatch[];
};

const STORAGE_VERSION = 1;

// A 4-hour round at one fix per 3s is ~4,800 fixes ≈ 10 batches. This holds roughly two
// unsent rounds before the oldest is dropped — well past the point where a device is
// simply never getting online.
const MAX_PENDING_BATCHES = 24;
const MAX_ATTEMPTS = 20;
const MAX_BATCH_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const STORAGE_KEY = `goffy:course-trace-queue:v${STORAGE_VERSION}`;

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

let storageOverride: AsyncStorageLike | null = null;

const storage = (): AsyncStorageLike | null => storageOverride ?? asyncStorage;

let envelope: Envelope = { version: STORAGE_VERSION, batches: [] };
let loaded = false;
let loadPromise: Promise<void> | null = null;

const normalize = (value: unknown): Envelope => {
  if (!value || typeof value !== 'object') return { version: STORAGE_VERSION, batches: [] };
  const input = value as Record<string, unknown>;
  if (input.version !== STORAGE_VERSION || !Array.isArray(input.batches)) {
    return { version: STORAGE_VERSION, batches: [] };
  }
  const batches = input.batches.filter((entry): entry is PendingBatch => {
    if (!entry || typeof entry !== 'object') return false;
    const candidate = entry as Record<string, unknown>;
    const batch = candidate.batch as CourseTraceBatch | undefined;
    return Boolean(batch && typeof batch.batchId === 'string' && Array.isArray(batch.points));
  });
  return { version: STORAGE_VERSION, batches };
};

const persist = async (): Promise<void> => {
  const store = storage();
  if (!store) return;
  await store.setItem(STORAGE_KEY, JSON.stringify(envelope));
};

const ensureLoaded = async (): Promise<void> => {
  if (loaded) return;
  if (!loadPromise) {
    loadPromise = (async () => {
      const store = storage();
      if (store) {
        try {
          const raw = await store.getItem(STORAGE_KEY);
          envelope = raw ? normalize(JSON.parse(raw)) : { version: STORAGE_VERSION, batches: [] };
        } catch {
          // A corrupt envelope is not worth failing a round over; start clean.
          envelope = { version: STORAGE_VERSION, batches: [] };
        }
      }
      loaded = true;
    })();
  }
  await loadPromise;
};

// Drops batches that have aged out or exhausted their retries. Without this a device
// that is permanently offline would grow its buffer without bound.
const applyGiveUpPolicy = (): number => {
  const cutoff = Date.now() - MAX_BATCH_AGE_MS;
  const before = envelope.batches.length;
  envelope.batches = envelope.batches.filter((entry) => {
    const enqueuedAtMs = Date.parse(entry.enqueuedAt);
    const tooOld = Number.isFinite(enqueuedAtMs) && enqueuedAtMs < cutoff;
    return !tooOld && entry.attempts < MAX_ATTEMPTS;
  });
  if (envelope.batches.length > MAX_PENDING_BATCHES) {
    envelope.batches = envelope.batches.slice(-MAX_PENDING_BATCHES);
  }
  return before - envelope.batches.length;
};

export const enqueueTraceBatch = async (batch: CourseTraceBatch): Promise<void> => {
  await ensureLoaded();
  if (envelope.batches.some((entry) => entry.batch.batchId === batch.batchId)) return;
  envelope.batches.push({ batch, enqueuedAt: new Date().toISOString(), attempts: 0 });
  applyGiveUpPolicy();
  await persist();
};

export const getPendingTraceBatchCount = async (): Promise<number> => {
  await ensureLoaded();
  return envelope.batches.length;
};

export type TraceUploader = (batch: CourseTraceBatch) => Promise<'accepted' | 'retry' | 'drop'>;

/**
 * Attempts to upload every pending batch. Batches that fail permanently are dropped;
 * batches that fail transiently keep their place and their attempt count.
 *
 * Returns the number of batches accepted by the server.
 */
export const flushTraceBatches = async (upload: TraceUploader): Promise<number> => {
  await ensureLoaded();
  if (envelope.batches.length === 0) return 0;

  let accepted = 0;
  const remaining: PendingBatch[] = [];

  // Snapshot first: each upload awaits, and the location task may enqueue new batches
  // while we are working. Those must survive the rebuild below.
  const snapshot = [...envelope.batches];
  const snapshotIds = new Set(snapshot.map((entry) => entry.batch.batchId));

  for (const entry of snapshot) {
    let outcome: 'accepted' | 'retry' | 'drop';
    try {
      outcome = await upload(entry.batch);
    } catch {
      outcome = 'retry';
    }

    if (outcome === 'accepted') {
      accepted += 1;
      continue;
    }
    if (outcome === 'drop') {
      // The server will never take it — a malformed batch, or one whose round the
      // caller turned out not to be in. Retrying forever would be pointless.
      continue;
    }
    remaining.push({ ...entry, attempts: entry.attempts + 1 });
  }

  const enqueuedDuringFlush = envelope.batches.filter(
    (entry) => !snapshotIds.has(entry.batch.batchId)
  );

  envelope.batches = [...remaining, ...enqueuedDuringFlush];
  applyGiveUpPolicy();
  await persist();
  return accepted;
};

export const __setCourseTraceStorageForTests = (store: AsyncStorageLike | null) => {
  storageOverride = store;
};

export const __resetCourseTraceQueueForTests = () => {
  envelope = { version: STORAGE_VERSION, batches: [] };
  loaded = false;
  loadPromise = null;
};
